import React from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Box,
  Button,
  IconButton,
  CircularProgress,
  Chip,
} from "@material-ui/core";
import { ArrowUpward, ArrowDownward } from "@material-ui/icons";
import { makeStyles } from "@material-ui/core/styles";
import QuestionTextBlock from "./QuestionTextBlock";

const useStyles = makeStyles(() => ({
  selectedQuestionsBox: {
    marginTop: 16,
  },
  selectedQuestionsHeaderRow: {
    display: "flex",
    flexWrap: "wrap",
    rowGap: 8,
    columnGap: 16,
    alignItems: "flex-start",
    justifyContent: "space-between",
    marginBottom: 8,
  },
  selectedQuestionsHeaderLeft: {
    display: "flex",
    flexDirection: "column",
  },
  selectedQuestionsHeaderRight: {
    display: "flex",
    flexDirection: "row",
    flexWrap: "wrap",
    rowGap: 8,
    columnGap: 16,
    alignItems: "center",
  },
  selectedQuestionRow: {
    border: "1px solid #ccc",
    borderRadius: 4,
    padding: "8px 12px",
    marginBottom: 8,
  },
  selectedQuestionHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
  },
  selectedQuestionTextWrap: {
    maxWidth: "calc(100% - 100px)",
  },
  selectedQuestionMetaRow: {
    marginTop: 4,
    display: "flex",
    flexWrap: "wrap",
    gap: 8,
    alignItems: "center",
  },
  selectedQuestionActionsCol: {
    minWidth: 90,
    marginLeft: 8,
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-end",
  },
  orderButtonsRow: {
    display: "flex",
    flexDirection: "row",
    columnGap: 4,
    marginBottom: 4,
  },
  orderIconBtn: {
    padding: 4,
    minWidth: 0,
  },
}));

export default function QuizEditorDialog({
  open,
  busy,
  mode, // "create" | "edit"
  name,
  desc,
  nameError,
  questions, // [{id, text/name, description, categoryLabel, expanded}, ...]
  onChangeName,
  onChangeDesc,
  onClose,
  onConfirm,
  onToggleExpandQuestion,
  onMoveUp,
  onMoveDown,
  onRemove,
  onAddQuestionClick,
}) {
  const classes = useStyles();

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>
        {mode === "edit" ? "Modifica quiz" : "Nuovo quiz"}
      </DialogTitle>

      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          label="Nome quiz"
          value={name}
          onChange={onChangeName}
          fullWidth
          variant="standard"
          error={nameError}
          helperText={nameError ? "Obbligatorio" : ""}
          disabled={busy}
        />

        <TextField
          margin="dense"
          label="Descrizione"
          value={desc}
          onChange={onChangeDesc}
          fullWidth
          variant="standard"
          multiline
          rows={3}
          disabled={busy}
        />

        <Box className={classes.selectedQuestionsBox}>
          <Box className={classes.selectedQuestionsHeaderRow}>
            <Box className={classes.selectedQuestionsHeaderLeft}>
              <Typography
                variant="subtitle1"
                gutterBottom
                style={{ margin: 0 }}
              >
                Domande nel quiz
              </Typography>
              <Typography
                variant="caption"
                color="textSecondary"
                style={{ lineHeight: 1.2 }}
              >
                Puoi riordinarle con le frecce
              </Typography>
            </Box>

            <Box className={classes.selectedQuestionsHeaderRight}>
              <Button
                variant="outlined"
                size="small"
                onClick={onAddQuestionClick}
                disabled={busy}
              >
                Aggiungi domanda
              </Button>
            </Box>
          </Box>

          {questions.length === 0 ? (
            <Typography
              variant="body2"
              color="textSecondary"
              style={{ fontStyle: "italic" }}
            >
              Nessuna domanda ancora associata
            </Typography>
          ) : (
            questions.map((q, idx) => {
              const qid = q.id;
              const title = q.text ?? q.name ?? "(senza testo)";
              const descText = q.description ?? "";
              const isFirst = idx === 0;
              const isLast = idx === questions.length - 1;

              return (
                <Box key={qid} className={classes.selectedQuestionRow}>
                  <Box className={classes.selectedQuestionHeader}>
                    <Box className={classes.selectedQuestionTextWrap}>
                      <QuestionTextBlock
                        title={title}
                        desc={descText}
                        expanded={q.expanded}
                        onToggleExpand={() => onToggleExpandQuestion(qid)}
                        disabledExpand={busy}
                      />

                      {q.categoryLabel && (
                        <Box className={classes.selectedQuestionMetaRow}>
                          <Chip size="small" label={q.categoryLabel} />
                        </Box>
                      )}
                    </Box>

                    <Box className={classes.selectedQuestionActionsCol}>
                      <Box className={classes.orderButtonsRow}>
                        <IconButton
                          className={classes.orderIconBtn}
                          size="small"
                          onClick={() => onMoveUp(qid)}
                          disabled={busy || isFirst}
                          aria-label="sposta su"
                        >
                          <ArrowUpward fontSize="small" />
                        </IconButton>
                        <IconButton
                          className={classes.orderIconBtn}
                          size="small"
                          onClick={() => onMoveDown(qid)}
                          disabled={busy || isLast}
                          aria-label="sposta giù"
                        >
                          <ArrowDownward fontSize="small" />
                        </IconButton>
                      </Box>

                      <Button
                        size="small"
                        onClick={() => onRemove(qid)}
                        disabled={busy}
                      >
                        Rimuovi
                      </Button>
                    </Box>
                  </Box>
                </Box>
              );
            })
          )}
        </Box>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={busy}>
          Chiudi
        </Button>
        <Box position="relative" display="inline-flex">
          <Button color="primary" onClick={onConfirm} disabled={busy}>
            Salva
          </Button>
          {busy && (
            <CircularProgress
              size={32}
              style={{
                position: "absolute",
                top: -6,
                right: -10,
                zIndex: 1,
              }}
            />
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
}
